// Deploy wizard — raw Ubuntu server to provisioned Outpost (doc 03 §3.2a).
//
// Connect (IP + SSH creds) -> Discover (live log, service tag, inventory) ->
// NIC roles -> Install -> Lockdown & verify -> provisioned, ready to ship.
//
// The credentials in this form are handed to the deploy job and never stored;
// there is no column for them.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api, type DeployJob, type Me, type Page, type Site } from "../api";
import {
  Badge,
  Card,
  Empty,
  ErrorNote,
  Field,
  PageHeader,
  Spinner,
  StatusDot,
  formatTime,
  useAction,
} from "../ui";

export default function DeployWizard({ me }: { me: Me }) {
  const { jobId } = useParams();
  return jobId ? <JobView jobId={Number(jobId)} me={me} /> : <StartForm />;
}

// --- step 1: connect -------------------------------------------------------
function StartForm() {
  const navigate = useNavigate();
  const { run, busy } = useAction();
  const [form, setForm] = useState({
    target_ip: "",
    ssh_user: "root",
    ssh_port: 22,
    ssh_password: "",
    ssh_private_key: "",
    expected_service_tag: "",
    site_id: "",
    repair_mode: false,
  });
  const [useKey, setUseKey] = useState(false);

  const { data: sites } = useQuery<Page<Site>>({
    queryKey: ["sites"],
    queryFn: () => api.get("/v1/sites"),
  });

  const { data: jobs } = useQuery<Page<DeployJob>>({
    queryKey: ["deploy-jobs"],
    queryFn: () => api.get("/v1/deploy?limit=20"),
    refetchInterval: 15_000,
  });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    run(async () => {
      const job = await api.post<DeployJob>("/v1/deploy", {
        target_ip: form.target_ip,
        ssh_user: form.ssh_user,
        ssh_port: Number(form.ssh_port),
        ssh_password: useKey ? null : form.ssh_password,
        ssh_private_key: useKey ? form.ssh_private_key : null,
        expected_service_tag: form.expected_service_tag || null,
        site_id: form.site_id ? Number(form.site_id) : null,
        repair_mode: form.repair_mode,
      });
      navigate(`/deploy/${job.id}`);
    });
  };

  return (
    <>
      <PageHeader
        title="Deploy new Outpost"
        subtitle="A raw Ubuntu 24.04 server on the staging network becomes a provisioned Outpost."
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,26rem)_1fr]">
        <Card title="Connect">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-[1fr_6rem] gap-3">
              <Field label="Staging IP address">
                <input
                  className="input"
                  required
                  autoFocus
                  placeholder="10.20.0.42"
                  value={form.target_ip}
                  onChange={(e) => setForm({ ...form, target_ip: e.target.value })}
                />
              </Field>
              <Field label="SSH port">
                <input
                  className="input"
                  type="number"
                  value={form.ssh_port}
                  onChange={(e) => setForm({ ...form, ssh_port: Number(e.target.value) })}
                />
              </Field>
            </div>

            <Field label="SSH user">
              <input
                className="input"
                required
                value={form.ssh_user}
                onChange={(e) => setForm({ ...form, ssh_user: e.target.value })}
              />
            </Field>

            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" className="accent-brand" checked={!useKey} onChange={() => setUseKey(false)} />
                Password
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" className="accent-brand" checked={useKey} onChange={() => setUseKey(true)} />
                Private key
              </label>
            </div>

            {useKey ? (
              <Field label="Private key">
                <textarea
                  className="input h-32 font-mono text-xs"
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                  value={form.ssh_private_key}
                  onChange={(e) => setForm({ ...form, ssh_private_key: e.target.value })}
                />
              </Field>
            ) : (
              <Field label="SSH password">
                <input
                  className="input"
                  type="password"
                  autoComplete="off"
                  value={form.ssh_password}
                  onChange={(e) => setForm({ ...form, ssh_password: e.target.value })}
                />
              </Field>
            )}

            <Field
              label="Expected service tag (optional)"
              hint="Guards against bootstrapping the wrong box — the deploy fails if the tag does not match."
            >
              <input
                className="input font-mono"
                placeholder="ABC1234"
                value={form.expected_service_tag}
                onChange={(e) => setForm({ ...form, expected_service_tag: e.target.value.toUpperCase() })}
              />
            </Field>

            <Field label="Assign to site (optional)" hint="Can also be set later during provisioning.">
              <select
                className="input"
                value={form.site_id}
                onChange={(e) => setForm({ ...form, site_id: e.target.value })}
              >
                <option value="">Not yet assigned</option>
                {sites?.items.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                    {site.customer_name ? ` — ${site.customer_name}` : ""}
                  </option>
                ))}
              </select>
            </Field>

            <label className="flex items-start gap-2 text-sm text-ink-300">
              <input
                type="checkbox"
                className="mt-1 accent-brand"
                checked={form.repair_mode}
                onChange={(e) => setForm({ ...form, repair_mode: e.target.checked })}
              />
              <span>
                Repair mode — reinstall the agent and containers on a box that is already enrolled, keeping
                its identity, site and config history.
              </span>
            </label>

            <div className="rounded-md border border-ink-600 bg-ink-900 p-3 text-xs text-ink-400">
              These credentials are handed to the deploy job and discarded when it finishes. They are never
              written to the database. Deploy only works on the staging network — in the field an Outpost
              has no inbound path, which is the point of the architecture.
            </div>

            <button className="btn-primary w-full" disabled={busy || !form.target_ip}>
              {busy ? "Starting…" : "Start deploy"}
            </button>
          </form>
        </Card>

        <Card title="Recent deploys" padded={false}>
          {!jobs?.items.length ? (
            <Empty>No deploys yet.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Target</th>
                    <th>Service tag</th>
                    <th>State</th>
                    <th>By</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {jobs.items.map((job) => (
                    <tr key={job.id}>
                      <td className="text-ink-400">{formatTime(job.started_at)}</td>
                      <td className="font-mono text-xs">
                        {job.ssh_user}@{job.target_ip}
                        {job.repair_mode && <Badge tone="info" className="ml-2">repair</Badge>}
                      </td>
                      <td className="service-tag">{job.discovered_service_tag ?? "—"}</td>
                      <td>
                        <StatusDot tone={jobTone(job.state)} label={job.state} pulse={job.state === "running"} />
                      </td>
                      <td className="text-ink-400">{job.started_by ?? "—"}</td>
                      <td className="text-right">
                        <Link to={`/deploy/${job.id}`} className="text-brand-on hover:underline">
                          view
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function jobTone(state: string) {
  return state === "succeeded" ? "ok" : state === "failed" ? "crit" : state === "running" ? "info" : "idle";
}

// --- steps 2-5: the live job ----------------------------------------------
function JobView({ jobId, me }: { jobId: number; me: Me }) {
  const queryClient = useQueryClient();
  const [lines, setLines] = useState<string[]>([]);
  const [live, setLive] = useState(true);
  const logRef = useRef<HTMLPreElement>(null);

  const { data: job, isLoading, error } = useQuery<DeployJob>({
    queryKey: ["deploy-job", jobId],
    queryFn: () => api.get(`/v1/deploy/${jobId}`),
    refetchInterval: (query) => (query.state.data?.state === "running" ? 5_000 : false),
  });

  // Live log over SSE. Replays what already happened, then follows — a viewer
  // who joins late still sees the whole build record.
  useEffect(() => {
    const source = new EventSource(`/v1/deploy/${jobId}/log`);
    source.onmessage = (event) => {
      const line = event.data as string;
      if (line.startsWith("__done__")) {
        setLive(false);
        queryClient.invalidateQueries({ queryKey: ["deploy-job", jobId] });
        source.close();
        return;
      }
      if (line.startsWith("__step__")) {
        queryClient.invalidateQueries({ queryKey: ["deploy-job", jobId] });
        return;
      }
      setLines((current) => [...current.slice(-4000), line]);
    };
    source.onerror = () => {
      setLive(false);
      source.close();
    };
    return () => source.close();
  }, [jobId, queryClient]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [lines]);

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!job) return null;

  const inventory = job.discovered_hardware_json;

  return (
    <>
      <PageHeader
        title={`Deploy #${job.id}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-4">
            <span className="font-mono">
              {job.ssh_user}@{job.target_ip}
            </span>
            {job.discovered_service_tag && (
              <span className="service-tag">{job.discovered_service_tag}</span>
            )}
            <StatusDot tone={jobTone(job.state)} label={job.state} pulse={job.state === "running"} />
          </span>
        }
        actions={
          job.endpoint_id && (
            <Link to={`/endpoints/${job.endpoint_id}`} className="btn-primary">
              Open Outpost
            </Link>
          )
        }
      />

      {job.error && (
        <div className="mb-6">
          <ErrorNote error={job.error} />
          <p className="mt-2 text-xs text-ink-400">
            Every step is idempotent — fix the cause and re-run the deploy against the same box.
          </p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_1fr]">
        <div className="space-y-6">
          <Card title="Steps" padded={false}>
            <ol className="divide-y divide-ink-700">
              {(job.steps_json ?? []).map((step) => (
                <li key={step.step} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                  <StepIcon state={step.state} />
                  <div className="min-w-0">
                    <div className={step.state === "ok" ? "text-ink-300" : "text-ink-100"}>{step.title}</div>
                    {step.detail && <div className="mt-0.5 text-xs text-state-crit">{step.detail}</div>}
                  </div>
                </li>
              ))}
              {!job.steps_json?.length && <li className="px-4 py-6 text-center text-sm text-ink-400">Connecting…</li>}
            </ol>
          </Card>

          {job.capability_json && (
            <Card title="Capability check">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-ink-400">Cores</dt>
                  <dd className="tabular-nums">{job.capability_json.cores}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink-400">Memory</dt>
                  <dd className="tabular-nums">{Math.round(job.capability_json.memory_mb / 1024)} GB</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink-400">Estimated hd QAM channels</dt>
                  <dd className="tabular-nums">{job.capability_json.estimated_qam_channels}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ink-400">NICs</dt>
                  <dd className="tabular-nums">{job.capability_json.nic_count}</dd>
                </div>
              </dl>
              {job.capability_json.warnings.length > 0 ? (
                <ul className="mt-3 space-y-1 rounded-md border border-state-warn/40 bg-state-warn/10 p-3 text-xs text-state-warn">
                  {job.capability_json.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-state-ok">This hardware clears the requirements.</p>
              )}
            </Card>
          )}

          {inventory?.nics && job.endpoint_id && me.role !== "reseller" && (
            <NicRolesStep job={job} onDone={() => queryClient.invalidateQueries({ queryKey: ["deploy-job", jobId] })} />
          )}
        </div>

        <Card
          title="Live log"
          actions={
            <span className="text-xs text-ink-400">
              {live ? "streaming" : job.state === "running" ? "reconnecting…" : "complete"}
            </span>
          }
          padded={false}
        >
          <pre ref={logRef} className="log-stream m-4 h-[34rem] whitespace-pre-wrap">
            {lines.length ? lines.join("\n") : (job.log_text ?? "Waiting for output…")}
          </pre>
        </Card>
      </div>
    </>
  );
}

function StepIcon({ state }: { state: string }) {
  if (state === "ok") return <span className="mt-0.5 text-state-ok">✓</span>;
  if (state === "failed") return <span className="mt-0.5 text-state-crit">✕</span>;
  if (state === "running")
    return <span className="mt-1 h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-ink-500 border-t-brand" />;
  return <span className="mt-0.5 text-ink-500">·</span>;
}

function NicRolesStep({ job, onDone }: { job: DeployJob; onDone: () => void }) {
  const nics = job.discovered_hardware_json?.nics ?? [];
  const [wan, setWan] = useState(job.suggested_wan_mac ?? "");
  const [lan, setLan] = useState("");
  const [addr, setAddr] = useState("");
  const { run, busy } = useAction();

  const assigned = job.nic_roles_json?.wan as { mac?: string } | undefined;
  if (assigned?.mac) {
    return (
      <Card title="NIC roles">
        <p className="text-sm text-state-ok">Roles assigned.</p>
      </Card>
    );
  }

  return (
    <Card title="NIC roles">
      <div className="space-y-3">
        <Field label="WAN" hint="Pre-suggested from the staging default route.">
          <select className="input" value={wan} onChange={(e) => setWan(e.target.value)}>
            <option value="">Select…</option>
            {nics.map((nic) => (
              <option key={nic.mac} value={nic.mac}>
                {nic.name} · {nic.mac}
                {nic.default_route ? " · default route" : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="LAN">
          <select className="input" value={lan} onChange={(e) => setLan(e.target.value)}>
            <option value="">Select…</option>
            {nics
              .filter((n) => n.mac !== wan)
              .map((nic) => (
                <option key={nic.mac} value={nic.mac}>
                  {nic.name} · {nic.mac} · {nic.speed_mbps ?? "?"} Mbps
                </option>
              ))}
          </select>
        </Field>
        <Field label="LAN address (CIDR)">
          <input className="input" placeholder="10.50.0.2/24" value={addr} onChange={(e) => setAddr(e.target.value)} />
        </Field>
        <button
          className="btn-primary w-full"
          disabled={busy || !wan || !lan}
          onClick={() =>
            run(async () => {
              await api.post(`/v1/deploy/${job.id}/nic_roles`, {
                wan_mac: wan,
                lan_mac: lan,
                lan_mode: "static",
                lan_addr: addr || null,
              });
              onDone();
            }, "NIC roles assigned")
          }
        >
          Assign roles
        </button>
      </div>
    </Card>
  );
}
